/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState, useEffect, useContext } from "react";
import { MARKETPLACE_DOMAIN_STATUS_WORDINGS, MARKETPLACE_DOMAIN_STATUS, TABLE_STYLES, MARKETPLACE_ORDER_SSP_UPDATE, REQUEST_GAM_INTERACTIVE_REPORT } from "./constants";
import { PauseCta, PlayCta as ResumeCta, RemoveDomainCta, RejectDomainCta, ApproveDomainCta } from "./CTA";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faPercent, faEye, faCircleInfo, faCoins, faDollarSign, faMousePointer } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { dequeueMarketplaceAction, enqueueMarketplaceAction } from "../../../reducers/marketplaceActionsQueueReducer";
import { pushMessage } from "../../../reducers/messagingReducer";
import { editMarketplaceOrder } from "../../../reducers/marketplaceOrdersReducer";
import { useMutation } from "@apollo/client";
import { MarketplaceGamDataContext } from "../../../context/marketplaceGamDataContext";
import { aggregateMetrics } from "./functions";

const BuyerDomainCtas = ({
    orderStatus,
    domain,
    pauseDomainLineItem,
    resumeDomainLineItem,
    order,
    orderIndex,
    currentCustomer,
    hasReachedDailyCap = false
}) => {
    let isApproved = orderStatus === MARKETPLACE_DOMAIN_STATUS.APPROVED;
    let isPending = orderStatus === MARKETPLACE_DOMAIN_STATUS.PENDING;
    let isPausedByBuyer = orderStatus === MARKETPLACE_DOMAIN_STATUS.PAUSED_BY_BUYER;
    let isResumed = orderStatus === MARKETPLACE_DOMAIN_STATUS.RESUMED;
    let isRejected = orderStatus === MARKETPLACE_DOMAIN_STATUS.REJECTED;
    const [isPausing, setIsPausing] = useState(false);
    const [isResuming, setIsResuming] = useState(false);
    const dispatch = useDispatch();

    if (isApproved || isResumed) {
        return (
            <div className="domain-ctas">
                <PauseCta
                    disabled={isPausing || hasReachedDailyCap}
                    showLoader={isPausing}
                    themeClasses="marketplace-cta outlined warning"
                    onClick={() => {
                        setIsPausing(true);
                        dispatch(
                            enqueueMarketplaceAction(async () => {
                                await pauseDomainLineItem(domain);
                                dispatch(dequeueMarketplaceAction());
                                setIsPausing(false);
                            })
                        );
                    }}
                />
            </div>
        );
    } else if (isPending || isRejected) {
        return (
            <div className="domain-ctas">
                <RemoveDomainCta
                    themeClasses="marketplace-cta outlined danger"
                    onClick={() => {
                        if (order.networks?.length === 1) {
                            dispatch(pushMessage("Marketplace", "There must be at least one domain selected for a marketplace order", "error"));
                            return;
                        }
                        let copy = { ...order };
                        copy.networks = copy.networks.filter((i) => i !== domain);
                        delete copy.networkStatuses[domain];
                        dispatch(editMarketplaceOrder(copy, orderIndex, currentCustomer));
                    }}
                />
            </div>
        );
    } else if (isPausedByBuyer) {
        return (
            <div className="domain-ctas">
                <ResumeCta
                    disabled={isResuming || hasReachedDailyCap}
                    showLoader={isResuming}
                    themeClasses="marketplace-cta outlined success"
                    onClick={() => {
                        setIsResuming(true);
                        dispatch(
                            enqueueMarketplaceAction(async () => {
                                await resumeDomainLineItem(domain);
                                dispatch(dequeueMarketplaceAction());
                                setIsResuming(false);
                            })
                        );
                    }}
                />
            </div>
        );
    } else {
        return null;
    }
};

/**
 * 
 * @param {Object} param0 
 * @param {Object.<string, DomainInformation>} param0.domainToNetworkMappings
 * @returns 
 */

const SellerDomainCtas = ({
    orderStatus,
    domain,
    rejectDomainHandler,
    approveDomainHandler,
    pauseDomainLineItem,
    resumeDomainLineItem,
    domainToNetworkMappings,
    hasReachedDailyCap = false
}) => {
    let isApproved = orderStatus === MARKETPLACE_DOMAIN_STATUS.APPROVED;
    let isPending = orderStatus === MARKETPLACE_DOMAIN_STATUS.PENDING;
    let isRejected = orderStatus === MARKETPLACE_DOMAIN_STATUS.REJECTED;
    let isPausedBySeller = orderStatus === MARKETPLACE_DOMAIN_STATUS.PAUSED_BY_SELLER;
    let isResumed = orderStatus === MARKETPLACE_DOMAIN_STATUS.RESUMED;
    let isAdapterDomain = !!domainToNetworkMappings[domain]?.isAdapterDomain
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [isPausing, setIsPausing] = useState(false);
    const [isResuming, setIsResuming] = useState(false);
    const dispatch = useDispatch();

    const resetState = () => {
        setIsApproving(false);
        setIsRejecting(false);
        setIsPausing(false);
        setIsResuming(false);
    };

    if (isApproved) {
        return (
            <div className="domain-ctas">
                <PauseCta
                    showLoader={isPausing}
                    disabled={isPausing || hasReachedDailyCap || isAdapterDomain}
                    themeClasses="outlined marketplace-cta warning"
                    onClick={() => {
                        setIsPausing(true);
                        dispatch(
                            enqueueMarketplaceAction(async () => {
                                await pauseDomainLineItem(domain);
                                dispatch(dequeueMarketplaceAction());
                                resetState();
                            })
                        );
                    }}
                />
            </div>
        );
    } else if (isPending) {
        return (
            <div className="domain-ctas">
                <ApproveDomainCta
                    disabled={isRejecting || isApproving || hasReachedDailyCap || isAdapterDomain}
                    showLoader={isApproving}
                    themeClasses="marketplace-cta outlined success"
                    onClick={() => {
                        setIsApproving(true);
                        dispatch(
                            enqueueMarketplaceAction(async () => {
                                await approveDomainHandler(domain);
                                dispatch(dequeueMarketplaceAction());
                                resetState();
                            })
                        );
                    }}
                />
                <RejectDomainCta
                    disabled={isRejecting || isApproving || hasReachedDailyCap || isAdapterDomain}
                    themeClasses="marketplace-cta outlined danger"
                    showLoader={isRejecting}
                    onClick={() => {
                        setIsRejecting(true);
                        rejectDomainHandler(domain).then(resetState).catch(resetState);
                    }}
                />
            </div>
        );
    } else if (isRejected) {
        return (
            <div className="domain-ctas">
                <ApproveDomainCta
                    disabled={isRejecting || isApproving || hasReachedDailyCap || isAdapterDomain}
                    showLoader={isApproving}
                    themeClasses="marketplace-cta outlined success"
                    onClick={() => {
                        setIsApproving(true);
                        dispatch(
                            enqueueMarketplaceAction(async () => {
                                await approveDomainHandler(domain);
                                dispatch(dequeueMarketplaceAction());
                                resetState();
                            })
                        );
                    }}
                />
            </div>
        );
    } else if (isResumed) {
        return (
            <div className="domain-ctas">
                <PauseCta
                    showLoader={isPausing}
                    disabled={isPausing || hasReachedDailyCap || isAdapterDomain}
                    themeClasses="outlined marketplace-cta warning"
                    onClick={() => {
                        setIsPausing(true);
                        dispatch(
                            enqueueMarketplaceAction(async () => {
                                await pauseDomainLineItem(domain);
                                dispatch(dequeueMarketplaceAction());
                                resetState();
                            })
                        );
                    }}
                />
            </div>
        );
    } else if (isPausedBySeller) {
        return (
            <div className="domain-ctas">
                <ResumeCta
                    showLoader={isResuming}
                    disabled={isResuming || hasReachedDailyCap || isAdapterDomain}
                    themeClasses="outlined marketplace-cta success"
                    onClick={() => {
                        setIsResuming(true);
                        dispatch(
                            enqueueMarketplaceAction(async () => {
                                await resumeDomainLineItem(domain);
                                dispatch(dequeueMarketplaceAction());
                                resetState();
                            })
                        );
                    }}
                />
            </div>
        );
    } else {
        return null;
    }
};

/**
 * 
 * @param {Object} param0 
 * @param {string[]} param0.domainList
 * @param {Object.<string, DomainInformation>} param0.domainToNetworkMappings
 * @param {MarketplaceOrder} param0.marketplaceOrder
 * @returns 
 */

const DomainStatusTable = ({
    domainList,
    domainToNetworkMappings,
    marketplaceOrder,
    isBuyer = true,
    rejectDomainHandler,
    approveDomainHandler,
    affectedDomains,
    pauseDomainLineItem,
    resumeDomainLineItem,
    orderIdx,
    currentCustomer,
    customerName
}) => {
    const lineItemStats = useSelector((state) => state.lineItemStatsReducer);
    /** @type {[Object.<string, DomainCampaignStats>, React.Dispatch<React.SetStateAction<Object.<string, DomainCampaignStats>>>]} */
    const [stats, setStats] = useState({});
    const [hasFinishedRequestingStats, setHasFinishedRequestingStats] = useState(false);
    const [hasImpressions, setHasImpressions] = useState(false);
    const [marketplaceOrderSspUpdate] = useMutation(MARKETPLACE_ORDER_SSP_UPDATE)
    const gamDataContext = useContext(MarketplaceGamDataContext)
    const { userID: userId } = useSelector((state) => state.userInformation);
    const [interactiveReportMutation] = useMutation(REQUEST_GAM_INTERACTIVE_REPORT)

    useEffect(() => {
        const startDate = new Date(marketplaceOrder.startDate)
        const endDate = new Date(marketplaceOrder.endDate)
        console.log({
            startDate,
            endDate
        })
        
        interactiveReportMutation({
            variables: {
                lineItemId: String(marketplaceOrder.lineItemId),
                startDate: `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()}`,
                endDate: `${endDate.getDate()}-${endDate.getMonth() + 1}-${endDate.getFullYear()}`
            }
        }).then(response => {
            let data = response.data.requestGoogleAdManagerReportMutation.gamApiResponse
            let parsed = JSON.parse(data)
            let jsonData = JSON.parse(parsed.data)
            console.log({jsonData})
        }).catch(err => {
            console.log({err})
        })
    }, [])

    useEffect(() => {
        let lineItemId = marketplaceOrder.lineItemId;
        let v = lineItemStats[lineItemId];
        setStats(v || {});
        setHasFinishedRequestingStats(true);
    }, [lineItemStats, marketplaceOrder]);

    useEffect(() => {
        if (Object.keys(stats).length) {
            setHasImpressions(
                domainList.some(domain =>
                    stats?.[domain]?.impressions
                )
            )
        }
    }, [stats])

    return marketplaceOrder?.networkStatuses ? (
        <table css={TABLE_STYLES}>
            <thead>
                <tr>
                    <th colSpan={1}>
                        <FontAwesomeIcon icon={faLink} />
                        &nbsp;&nbsp;Domain
                    </th>
                    <th colSpan={1}>
                        <FontAwesomeIcon icon={faEye} />
                        &nbsp;&nbsp;Impressions
                    </th>
                    <th colSpan={1}>
                        <FontAwesomeIcon icon={faMousePointer} />
                        &nbsp;&nbsp;Clicks
                    </th>
                    <th colSpan={1}>
                        <FontAwesomeIcon icon={faPercent} />
                        &nbsp;&nbsp;CTR
                    </th>
                    {!isBuyer && hasImpressions && (
                        <th colSpan={1}>
                            <FontAwesomeIcon icon={faCoins} /> &nbsp;&nbsp;CPM
                        </th>
                    )}
                    {!isBuyer && (
                        <th colSpan={1}>
                            <FontAwesomeIcon icon={faDollarSign} /> &nbsp;&nbsp;Revenue
                        </th>
                    )}
                    <th colSpan={1}>
                        <FontAwesomeIcon icon={faCircleInfo} />
                        &nbsp;&nbsp;Status
                    </th>
                    <th colSpan={1}></th>
                </tr>
            </thead>
            <tbody>
                {domainList?.map((domain) => {
                    let status = marketplaceOrder?.networkStatuses?.[domain]?.status;
                    let domainStats = stats?.[domain]
                    let impressions = domainStats?.impressions || "N/A";
                    let formattedImpressions = impressions === "N/A" ? impressions : parseInt(impressions).toLocaleString();
                    let clicks = domainStats?.clicks ?? "N/A";
                    let ctr = parseFloat(domainStats?.ctr) ?? "N/A";
                    ctr = isNaN(ctr) ? 'N/A' : ctr
                    let costPerUnit = domainStats?.ecpm;
                    let revenue = domainStats?.revenue;
                    let hasReachedDailyCap = !!marketplaceOrder?.orderPausedByAgentScript

                    return (
                        <tr key={domain}>
                            <td>{domainToNetworkMappings?.[domain]?.domainName}</td>
                            <td>{hasFinishedRequestingStats ? formattedImpressions : <i className="pi pi-spin pi-spinner" style={{ fontSize: "1.5em" }}></i>}</td>
                            <td>{clicks}</td>
                            <td>{hasFinishedRequestingStats ? ctr : <i className="pi pi-spin pi-spinner" style={{ fontSize: "1.5em" }}></i>}</td>
                            {!isBuyer && hasImpressions && (
                                <td className="cpm">
                                    <b>{costPerUnit || marketplaceOrder.CPM}</b>
                                </td>
                            )}
                            {!isBuyer && (
                                <td className="revenue">
                                    <b>{revenue}</b>
                                </td>
                            )}
                            {
                                hasReachedDailyCap
                                    ? <td>Order reached daily limit</td>
                                    : <td className={status}>{MARKETPLACE_DOMAIN_STATUS_WORDINGS?.[status]} </td>
                            }

                            <td>
                                {isBuyer ? (
                                    <BuyerDomainCtas
                                        orderStatus={status}
                                        pauseDomainLineItem={pauseDomainLineItem}
                                        resumeDomainLineItem={resumeDomainLineItem}
                                        domain={domain}
                                        order={marketplaceOrder}
                                        orderIndex={orderIdx}
                                        currentCustomer={currentCustomer}
                                        hasReachedDailyCap={hasReachedDailyCap}
                                    />
                                ) : (
                                    <SellerDomainCtas
                                        domain={domain}
                                        orderStatus={status}
                                        affectedDomains={affectedDomains}
                                        hasReachedDailyCap={hasReachedDailyCap}
                                        domainToNetworkMappings={domainToNetworkMappings}
                                        approveDomainHandler={async () => {
                                            /** @type {string} */
                                            let orderStatus = gamDataContext.gamStats[marketplaceOrder.lineItemId]?.status || "ready"
                                            let variables = {
                                                orderId: String(marketplaceOrder.wpMarketplacePostId),
                                                domainId: String(domain),
                                                data: JSON.stringify({
                                                    customer_name: customerName
                                                }),
                                                op: "APPROVE",
                                                orderStatus: orderStatus.toLowerCase(),
                                                userId: String(userId)

                                            }
                                            await approveDomainHandler(domain);
                                            await marketplaceOrderSspUpdate({
                                                variables
                                            })
                                        }}
                                        rejectDomainHandler={async () => {
                                            await rejectDomainHandler(domain)
                                        }}
                                        pauseDomainLineItem={async () => {
                                            /** @type {string} */
                                            let orderStatus = gamDataContext.gamStats[marketplaceOrder.lineItemId]?.status || "ready"

                                            let variables = {
                                                orderId: String(marketplaceOrder.wpMarketplacePostId),
                                                domainId: String(domain),
                                                data: JSON.stringify({
                                                    customer_name: customerName
                                                }),
                                                op: "PAUSE",
                                                orderStatus: orderStatus.toLowerCase(),
                                                userId: String(userId)
                                            }
                                            await pauseDomainLineItem(domain);
                                            await marketplaceOrderSspUpdate({
                                                variables
                                            });
                                        }}
                                        resumeDomainLineItem={async () => {
                                            /** @type {string} */
                                            let orderStatus = gamDataContext.gamStats[marketplaceOrder.lineItemId]?.status || "ready"
                                            let variables = {
                                                orderId: String(marketplaceOrder.wpMarketplacePostId),
                                                domainId: String(domain),
                                                data: JSON.stringify({
                                                    customer_name: customerName
                                                }),
                                                op: "RESUME",
                                                orderStatus: orderStatus.toLowerCase(),
                                                userId: String(userId)
                                            }
                                            await resumeDomainLineItem(domain);
                                            await marketplaceOrderSspUpdate({
                                                variables
                                            });
                                        }}
                                    />
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    ) : null;
};

export default DomainStatusTable;
